import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Paperclip, Plus, Search, ShieldAlert, ShieldQuestion, Trash2 } from 'lucide-react';
import {
  CERT_STANDARD_LABEL,
  CERT_REVIEW_LABEL,
  CERT_STATUS_LABEL,
  UserRole,
  type CertificationDto,
  type CertStandard,
  type SupplierListResponseDto,
  type SupplierStoreListItemDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useUrlFilter } from '../../lib/urlFilter';
import { useAuth } from '../../lib/auth';
import { formatDate } from '../../lib/format';
import { FilePicker } from '../../components/FilePicker';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  SortHeader,
  Textarea,
} from '../../components/ui';
import { useTableSort } from '../../lib/sorting';

/**
 * `/admin/certificacoes` — CRUD e fila de validação (PLAN §7, tela 9).
 *
 * Não existe campo de nota nem de demérito nesta tela, e isso é o ponto: o ISQ
 * sai da combinação de normas vigentes. O que uma pessoa decide aqui é se o
 * documento **vale**, nunca quanto ele pontua.
 *
 * A fila de pendentes vem primeiro porque é a única coisa aqui que trava a nota
 * de alguém: enquanto o certificado não é validado, ele não conta.
 *
 * **O cadastro pela Weloze só chegou na fatia (l).** Até então esta tela era a
 * fila de validação e mais nada: a ficha do fornecedor dizia "cadastrar e
 * aprovar seguem na tela de Certificações", esta dizia "os certificados são
 * cadastrados pela ficha de cada fornecedor", e as duas apontavam uma para a
 * outra. Não havia formulário em lugar nenhum — a Weloze só conseguia aprovar o
 * que o fornecedor enviasse, e o certificado que chegava por e-mail não tinha
 * como entrar.
 */

/**
 * Faixa de vencimento — e **só** ela.
 *
 * O rótulo era "Situação" e o controle perguntava duas coisas ao mesmo tempo:
 * quanto falta para vencer e em que pé está a análise. Quem procurava um
 * certificado recusado não tinha onde pedir isso, e quem escolhia "Vigentes"
 * recebia junto os que ninguém validou ainda.
 */
type Filter = 'all' | 'valid' | 'expiring' | 'expired';

const FILTER_LABEL: Record<Filter, string> = {
  all: 'Qualquer prazo',
  valid: 'Vigentes',
  expiring: 'A vencer em 60 dias',
  expired: 'Vencidos',
};

/** A outra metade da pergunta antiga: em que pé está a análise. */
type Situacao = 'all' | 'APPROVED' | 'PENDING' | 'REJECTED';

const SITUACAO_LABEL: Record<Situacao, string> = {
  all: 'Todas',
  APPROVED: 'Validados',
  PENDING: 'Aguardando validação',
  REJECTED: 'Recusados',
};

const COLUNAS = {
  fornecedor: (cert: CertificationDto) => cert.supplierName ?? '',
  norma: (cert: CertificationDto) => CERT_STANDARD_LABEL[cert.standard],
  vencimento: (cert: CertificationDto) => cert.validUntil,
  situacao: (cert: CertificationDto) => cert.daysUntilExpiry,
};

export function CertificationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [filter, setFilter] = useState<Filter>('all');
  const [situacao, setSituacao] = useState<Situacao>('all');
  // O contador “certificados a validar” do painel chega por aqui.
  useUrlFilter('situacao', ['all', 'APPROVED', 'PENDING', 'REJECTED'] as const, setSituacao);
  const [norma, setNorma] = useState('');
  const [busca, setBusca] = useState('');
  const [items, setItems] = useState<CertificationDto[] | null>(null);
  const [pending, setPending] = useState<CertificationDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<CertificationDto | null>(null);
  const [criando, setCriando] = useState(false);
  const { ask, dialog } = useConfirm();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [lista, fila] = await Promise.all([
        api.get<CertificationDto[]>(`/certifications?filter=${filter}`),
        api.get<CertificationDto[]>('/certifications?reviewStatus=PENDING'),
      ]);
      setItems(lista);
      setPending(fila);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar.');
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  // As normas saem da própria carteira: um seletor com toda a lista de normas
  // possíveis, onde só três aparecem, faz procurar no vazio.
  const normas = useMemo(
    () => [...new Set((items ?? []).map((cert) => cert.standard))].sort(),
    [items],
  );

  // Filtrados no navegador porque a listagem já vem inteira do servidor: mandar
  // norma e busca para lá seria uma ida ao banco para não mudar nada.
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (items ?? []).filter((cert) => {
      if (norma && cert.standard !== norma) return false;
      if (situacao !== 'all' && cert.reviewStatus !== situacao) return false;
      if (!termo) return true;
      return [cert.supplierName, cert.protheusRef, cert.number]
        .some((campo) => (campo ?? '').toLowerCase().includes(termo));
    });
  }, [items, norma, situacao, busca]);

  const { rows, sort, toggle } = useTableSort(visiveis, COLUNAS, {
    // O que vence primeiro aparece primeiro: a tela existe para não deixar
    // vencer.
    key: 'vencimento',
    direction: 'asc',
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Certificações</h1>
          <p className="mt-1 text-sm text-slate-600">
            O ISQ é derivado das normas vigentes no último dia da competência. Não há nota a digitar
            aqui — só o documento a validar.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCriando(true)}>
            <Plus className="size-4" aria-hidden />
            Cadastrar certificado
          </Button>
        )}
      </header>

      {error && <Alert>{error}</Alert>}

      {pending.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <div className="flex items-center gap-2">
            <ShieldQuestion className="size-4 text-amber-600" aria-hidden />
            <h2 className="font-medium text-slate-900">
              {pending.length} certificado{pending.length === 1 ? '' : 's'} aguardando validação
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Enquanto não forem validados, não contam no ISQ de ninguém.
          </p>
          <ul className="mt-3 space-y-2">
            {pending.map((cert) => (
              <li
                key={cert.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {cert.supplierName} · {CERT_STANDARD_LABEL[cert.standard]}
                  </p>
                  <p className="text-xs text-slate-500">
                    {cert.protheusRef} · vence em {formatDate(cert.validUntil)}
                  </p>
                </div>
                {isAdmin && (
                  <Button size="sm" onClick={() => setReviewing(cert)}>
                    Analisar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Fornecedor"
          name="busca"
          className="w-64"
          icon={<Search className="size-4" aria-hidden />}
          placeholder="Nome, código ou nº do certificado"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <Select
          label="Vencimento"
          name="filtro"
          className="w-52"
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          {(Object.keys(FILTER_LABEL) as Filter[]).map((value) => (
            <option key={value} value={value}>
              {FILTER_LABEL[value]}
            </option>
          ))}
        </Select>
        <Select
          label="Norma"
          name="norma"
          className="w-44"
          value={norma}
          onChange={(e) => setNorma(e.target.value)}
        >
          <option value="">Todas</option>
          {normas.map((valor) => (
            <option key={valor} value={valor}>
              {CERT_STANDARD_LABEL[valor]}
            </option>
          ))}
        </Select>
        <Select
          label="Situação"
          name="situacao"
          className="w-52"
          value={situacao}
          onChange={(e) => setSituacao(e.target.value as Situacao)}
        >
          {(Object.keys(SITUACAO_LABEL) as Situacao[]).map((value) => (
            <option key={value} value={value}>
              {SITUACAO_LABEL[value]}
            </option>
          ))}
        </Select>
      </div>

      {!items ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : visiveis.length === 0 ? (
        <EmptyState
          title="Nenhum certificado nesta situação"
          description={
            'Um fornecedor sem certificação vigente fica com ISQ 0 — é um degrau legítimo da ' +
            'régua, mas o pior deles.'
          }
          action={
            isAdmin ? <Button onClick={() => setCriando(true)}>Cadastrar certificado</Button> : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[48rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <SortHeader column="fornecedor" sort={sort} onSort={toggle}>
                  Fornecedor
                </SortHeader>
                <SortHeader column="norma" sort={sort} onSort={toggle}>
                  Norma
                </SortHeader>
                <SortHeader column="vencimento" sort={sort} onSort={toggle}>
                  Vencimento
                </SortHeader>
                <SortHeader column="situacao" sort={sort} onSort={toggle}>
                  Situação
                </SortHeader>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((cert) => (
                <tr key={cert.id} className="transition-colors hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/fornecedores/${cert.storeId}`}
                      className="font-medium text-brand-700 underline-offset-4 hover:underline"
                    >
                      {cert.supplierName}
                    </Link>
                    <p className="font-mono text-xs text-slate-500">{cert.protheusRef}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-slate-700">
                      {CERT_STANDARD_LABEL[cert.standard]}
                      {cert.isIatfRoadmap && <Badge tone="accent">cronograma</Badge>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(cert.validUntil)}</td>
                  <td className="px-4 py-3">
                    <ExpiryBadge cert={cert} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* O download é rota autenticada, e por isso vira linha em
                          `file_accesses` como qualquer outra — inclusive quando
                          quem abre é a Weloze. */}
                      {cert.fileId && (
                        <a
                          href={`/api/files/${cert.fileId}`}
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-700"
                          aria-label={`Abrir o arquivo do certificado de ${cert.supplierName}`}
                        >
                          <Paperclip className="size-4" aria-hidden />
                        </a>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          aria-label={`Excluir o certificado ${CERT_STANDARD_LABEL[cert.standard]} de ${cert.supplierName}`}
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          onClick={() =>
                            ask({
                              title: 'Excluir certificado',
                              body: (
                                <>
                                  <p>
                                    <strong>{CERT_STANDARD_LABEL[cert.standard]}</strong> de{' '}
                                    <strong>{cert.supplierName}</strong>, com validade até{' '}
                                    {formatDate(cert.validUntil)}.
                                  </p>
                                  <p>
                                    O ISQ desta loja é recalculado sem ele{' '}
                                    <strong>a partir da próxima competência</strong>. As
                                    competências já fechadas guardam o número como foi apurado e
                                    não mudam.
                                  </p>
                                </>
                              ),
                              confirmLabel: 'Excluir',
                              onConfirm: async () => {
                                await api.delete(`/certifications/${cert.id}`);
                                await load();
                              },
                            })
                          }
                        >
                          <Trash2 className="size-4" aria-hidden />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateModal
        open={criando}
        onClose={() => setCriando(false)}
        onDone={() => {
          setCriando(false);
          void load();
        }}
      />

      {dialog}

      <ReviewModal
        certification={reviewing}
        onClose={() => setReviewing(null)}
        onDone={() => {
          setReviewing(null);
          void load();
        }}
      />
    </div>
  );
}

/** Um selo só, que junta validade e situação da análise. */
function ExpiryBadge({ cert }: { cert: CertificationDto }) {
  if (cert.reviewStatus !== 'APPROVED') {
    return <Badge tone={cert.reviewStatus === 'PENDING' ? 'warning' : 'danger'}>
      {CERT_REVIEW_LABEL[cert.reviewStatus]}
    </Badge>;
  }
  if (cert.status !== 'VALID') {
    return <Badge tone="danger">{CERT_STATUS_LABEL[cert.status]}</Badge>;
  }
  if (cert.daysUntilExpiry < 0) {
    return (
      <Badge tone="danger">
        <ShieldAlert className="size-3" aria-hidden />
        vencido há {Math.abs(cert.daysUntilExpiry)} dias
      </Badge>
    );
  }
  if (cert.daysUntilExpiry <= 60) {
    return <Badge tone="warning">vence em {cert.daysUntilExpiry} dias</Badge>;
  }
  return (
    <Badge tone="success">
      <BadgeCheck className="size-3" aria-hidden />
      vigente
    </Badge>
  );
}

function ReviewModal({
  certification,
  onClose,
  onDone,
}: {
  certification: CertificationDto | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReason('');
    setError(null);
  }, [certification]);

  async function review(approve: boolean) {
    if (!certification) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/certifications/${certification.id}/review`, { approve, reason: reason || null });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) setError(err.fields?.reason ?? err.message);
      else setError('Não foi possível registrar a análise.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={certification !== null}
      onClose={onClose}
      title="Validar certificado"
      description={
        certification
          ? `${certification.supplierName} · ${CERT_STANDARD_LABEL[certification.standard]}, vence em ${formatDate(certification.validUntil)}.`
          : undefined
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void review(true);
        }}
      >
        <p className="text-sm text-slate-600">
          Aprovar faz o certificado passar a contar no ISQ a partir da próxima competência
          calculada. Recusar exige um motivo — é o que o fornecedor vai ler.
        </p>
        <Textarea
          label="Motivo da recusa"
          name="reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={error ?? undefined}
          hint="Obrigatório apenas ao recusar."
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="outline" loading={saving} onClick={() => void review(false)}>
            Recusar
          </Button>
          <Button type="submit" loading={saving}>
            Aprovar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Cadastro pela Weloze
// ---------------------------------------------------------------------------

/**
 * Cadastro de certificado pela Qualidade.
 *
 * O caso real é o certificado que chega por e-mail: alguém precisa registrá-lo,
 * e até a fatia (l) não havia onde. O que o fornecedor manda pelo portal entra
 * **pendente**; o que a Weloze cadastra aqui já nasce validado — quem decide é o
 * papel de quem está logado, dentro do serviço, e não um seletor nesta tela.
 *
 * **Não há campo de nota, de percentual nem de demérito, e não pode passar a
 * haver.** O ISQ sai da combinação de normas vigentes na data de corte. Foi um
 * campo livre de "nota percentual" na planilha antiga que fez a ALCAST aparecer
 * com 15% de demérito e nota 80, quando o correto é 85.
 *
 * O fornecedor é escolhido **antes** do arquivo porque o arquivo pertence a uma
 * loja: o envio é para `/stores/:storeId/files`, e sem loja não há para onde
 * enviar.
 */
function CreateModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [lojas, setLojas] = useState<SupplierStoreListItemDto[] | null>(null);
  const [form, setForm] = useState({
    storeId: '',
    standard: 'ISO_9001' as CertStandard,
    number: '',
    certifyingBody: '',
    issuedAt: '',
    validUntil: '',
    isIatfRoadmap: false,
    roadmapPresentedAt: '',
    roadmapAuditScheduledAt: '',
    notes: '',
    fileId: null as string | null,
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Zerar a cada abertura: sem isto o segundo cadastro herdaria o fornecedor e a
  // data do primeiro, e um certificado atribuído à loja errada é exatamente o
  // tipo de erro que só aparece na competência seguinte.
  useEffect(() => {
    if (!open) return;
    setForm({
      storeId: '',
      standard: 'ISO_9001',
      number: '',
      certifyingBody: '',
      issuedAt: '',
      validUntil: '',
      isIatfRoadmap: false,
      roadmapPresentedAt: '',
      roadmapAuditScheduledAt: '',
      notes: '',
      fileId: null,
    });
    setFields({});
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || lojas) return;
    let ativo = true;
    api
      .get<SupplierListResponseDto>('/suppliers?scope=monitored')
      .then((resposta) => {
        if (ativo) setLojas(resposta.items);
      })
      .catch(() => {
        if (ativo) setError('Não foi possível carregar a lista de fornecedores.');
      });
    return () => {
      ativo = false;
    };
  }, [open, lojas]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);
    setError(null);
    setFields({});
    try {
      await api.post(`/stores/${form.storeId}/certifications`, {
        standard: form.standard,
        number: form.number || null,
        certifyingBody: form.certifyingBody || null,
        issuedAt: form.issuedAt || null,
        validUntil: form.validUntil,
        isIatfRoadmap: form.isIatfRoadmap,
        roadmapPresentedAt: form.roadmapPresentedAt || null,
        roadmapAuditScheduledAt: form.roadmapAuditScheduledAt || null,
        notes: form.notes || null,
        fileId: form.fileId,
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível cadastrar o certificado.');
      }
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Cadastrar certificado"
      description="Cadastro pela Weloze nasce validado e passa a contar no ISQ na próxima apuração."
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Select
          label="Fornecedor"
          name="storeId"
          required
          value={form.storeId}
          onChange={(e) => setForm({ ...form, storeId: e.target.value, fileId: null })}
          error={fields.storeId}
          hint={lojas ? undefined : 'Carregando as lojas monitoradas…'}
        >
          <option value="">Selecione…</option>
          {(lojas ?? []).map((loja) => (
            <option key={loja.storeId} value={loja.storeId}>
              {loja.companyName} — {loja.protheusRef}
            </option>
          ))}
        </Select>

        <Select
          label="Norma"
          name="standard"
          value={form.standard}
          onChange={(e) => {
            const standard = e.target.value as CertStandard;
            setForm({
              ...form,
              standard,
              // O cronograma é sempre da IATF: trocar de norma sem limpar a
              // marcação faria o formulário pedir datas de um cronograma que o
              // servidor recusaria.
              isIatfRoadmap: standard === 'IATF_16949' ? form.isIatfRoadmap : false,
            });
          }}
          error={fields.standard}
        >
          {(Object.keys(CERT_STANDARD_LABEL) as CertStandard[]).map((valor) => (
            <option key={valor} value={valor}>
              {CERT_STANDARD_LABEL[valor]}
            </option>
          ))}
        </Select>

        <div className="flex flex-wrap gap-3">
          <Field
            label="Número do certificado"
            name="number"
            className="min-w-[12rem] flex-1"
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            error={fields.number}
          />
          <Field
            label="Organismo certificador"
            name="certifyingBody"
            className="min-w-[12rem] flex-1"
            value={form.certifyingBody}
            onChange={(e) => setForm({ ...form, certifyingBody: e.target.value })}
            error={fields.certifyingBody}
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <Field
            label="Emitido em"
            name="issuedAt"
            type="date"
            className="w-52"
            value={form.issuedAt}
            onChange={(e) => setForm({ ...form, issuedAt: e.target.value })}
            error={fields.issuedAt}
          />
          <Field
            label="Válido até"
            name="validUntil"
            type="date"
            className="w-52"
            required
            value={form.validUntil}
            onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
            error={fields.validUntil}
            hint="Comparada com o último dia de cada competência."
          />
        </div>

        {form.standard === 'IATF_16949' && (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.isIatfRoadmap}
                onChange={(e) => setForm({ ...form, isIatfRoadmap: e.target.checked })}
                className="mt-0.5 size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                É um <strong>cronograma</strong> de implantação, e não o certificado IATF.
                <span className="mt-0.5 block text-xs text-slate-500">
                  Vale 10% de demérito em vez de 20%, e só enquanto a auditoria agendada não passar.
                </span>
              </span>
            </label>

            {form.isIatfRoadmap && (
              <div className="flex flex-wrap gap-3">
                <Field
                  label="Cronograma apresentado em"
                  name="roadmapPresentedAt"
                  type="date"
                  className="w-52"
                  value={form.roadmapPresentedAt}
                  onChange={(e) => setForm({ ...form, roadmapPresentedAt: e.target.value })}
                  error={fields.roadmapPresentedAt}
                />
                <Field
                  label="Auditoria agendada para"
                  name="roadmapAuditScheduledAt"
                  type="date"
                  className="w-52"
                  value={form.roadmapAuditScheduledAt}
                  onChange={(e) => setForm({ ...form, roadmapAuditScheduledAt: e.target.value })}
                  error={fields.roadmapAuditScheduledAt}
                  hint="Sem ela o benefício nunca expira."
                />
              </div>
            )}
          </div>
        )}

        {form.storeId ? (
          <FilePicker
            storeId={form.storeId}
            label="Arquivo do certificado"
            value={form.fileId}
            onChange={(fileId) => setForm({ ...form, fileId })}
          />
        ) : (
          <p className="text-sm text-slate-500">
            Escolha o fornecedor para anexar o arquivo — ele é guardado na pasta da loja.
          </p>
        )}

        <Textarea
          label="Observações"
          name="notes"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          error={fields.notes}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvando} disabled={!form.storeId}>
            Cadastrar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
